import * as React from 'react';
import { inject, observer } from 'mobx-react';
import './sessionListPane.module.scss';
import { CalendarView } from '../../molecules/calendar/CalendarView';
import { DateGroup } from '../../molecules/reservable/DateGroup';
import { ScheduleListItem } from '../../molecules/reservable/ScheduleListItem';
import { groupByDate, ReservableType } from '../../../../commonv3/types/reservable';
import { BackButton } from '../../../../commonv3/components/atoms/buttons/BackButton';
import moment from 'moment-timezone';
import { Button } from '../../../../commonv3/components/atoms/buttons/Button';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { V3Routes } from '../../../routes';
import { isFullSession, SessionIndexView, SessionType } from '../../../types/sessions';
import { HomePageState } from '../../../containers/home/states/HomePageState';
import { parseQueryParams } from '../../../types/location';
import { getLocationSelector, getOnlineSelector, LocationSelector } from '../../molecules/forms/LocationSelector';
import { observable } from 'mobx';
import { SessionWaitlistState } from './states/SessionWaitlistState';
import { LoaderRow } from '../../atoms/layout/LoaderRow';
import { SessionWaitlistDescription } from '../../molecules/session_waitlists/SessionWaitlistDescription';

type Props = {
    homeState: HomePageState;
};

type WailtlistToggleButtonProps = {
    entry: SessionIndexView;
    reserved: boolean;
    sessionWaitlistState: SessionWaitlistState;
};

export const WaitlistToggleButton = observer(
    ({ entry, reserved, sessionWaitlistState }: WailtlistToggleButtonProps) => {
        const {
            submitting,
            sessionWaitlists,
            createSessionWaitingList,
            cancelSessionWaitingList,
        } = sessionWaitlistState;
        if (submitting) {
            return <LoaderRow />;
        }
        if (!sessionWaitlistState.canShowWaitlist(entry, reserved)) {
            return <div />;
        }

        const sessionWaitlist = sessionWaitlists.getOrElse([]).find((waitlist) => waitlist.session_id === entry.id);
        return sessionWaitlist ? (
            <div styleName="cancel-waitlist" onClick={() => cancelSessionWaitingList(sessionWaitlist.id)}>
                <span id="destroy-waitlist" styleName="waitlist">
                    空き枠通知をOFFにする
                </span>
            </div>
        ) : (
            <div styleName="create-waitlist" onClick={() => createSessionWaitingList(entry.id)}>
                <span id="create-waitlist" styleName="waitlist">
                    空き枠通知をONにする
                </span>
            </div>
        );
    }
);

@inject('homeState')
@observer
export class SessionListPane extends React.Component<Props> {
    @observable sessionWaitlistState = new SessionWaitlistState();

    async componentDidMount() {
        const { sessionList } = this.props.homeState;
        const { sessionType = SessionType.GROUP_COACHING } = parseQueryParams(GlobalStatesV3.routing.location);
        const { homeLocation } = GlobalStatesV3.currentUser;
        await homeLocation.reload();
        const { isHomeLocationSet, isCurrentLocationOnline, locationId } = homeLocation;
        sessionList.setSessionType(sessionType);
        await Promise.all([this.sessionWaitlistState.reloadSessionWaitlists(), this.props.homeState.reloadLocations()]);
        sessionList.setSelectedLocationSelectors(
            isHomeLocationSet && !isCurrentLocationOnline
                ? [getLocationSelector(locationId), getOnlineSelector()]
                : [getOnlineSelector()]
        );
    }

    render() {
        const { locations, reservationList, sessionList } = this.props.homeState;
        const {
            sessionsOfType,
            filteredSessions,
            sessionType,
            selectedLocationSelectors,
            setSelectedLocationSelectors,
            selectedDate,
            setSelectedDate,
            setSelectedMonth,
        } = sessionList;
        const groups = groupByDate(filteredSessions, (r) => r.starts_at);
        const { availableTokens } = GlobalStatesV3.currentUser;
        const isHomeLocationSet = GlobalStatesV3.currentUser.homeLocation.isHomeLocationSet;
        const homeLocationId = GlobalStatesV3.currentUser.homeLocation.locationId;

        return (
            <div id="session-pane" styleName="pane">
                {GlobalStatesV3.viewport.isMobile && <BackButton />}
                <div styleName="pane-header">
                    <div styleName="pane-title">
                        {sessionType === 'group_coaching' ? 'コーチングの予約' : 'もくもく会の予約'}
                    </div>
                    <div styleName="pane-header-border" />
                    <SessionWaitlistDescription />
                </div>
                <div styleName="views">
                    <div styleName="calendar-wrapper">
                        <CalendarView
                            startDate={moment().date(1)}
                            endDate={moment()
                                .add(6, 'month')
                                .date(1)}
                            selectedDate={selectedDate}
                            clickables={sessionsOfType.map((r) => r.starts_at)}
                            onClick={setSelectedDate}
                            onMonthChange={setSelectedMonth}
                        />
                    </div>
                    <div styleName="schedule-list-wrapper">
                        <div styleName="schedule-list-header">
                            <div styleName="schedule-list-header-box">
                                <div styleName="schedule-list-header-text">表示する拠点</div>
                                <LocationSelector
                                    locations={locations.getOrElse([])}
                                    homeLocationId={homeLocationId}
                                    isHomeLocationSet={isHomeLocationSet}
                                    showOther={false}
                                    showOnline={true}
                                    showOnlineFirst={true}
                                    locationSelectors={selectedLocationSelectors}
                                    onChange={setSelectedLocationSelectors}
                                />
                            </div>
                        </div>
                        <div styleName="schedule-list-body">
                            {groups.map((group) => (
                                <DateGroup key={group.date} date={group.date}>
                                    {group.entries.map((entry) => {
                                        const reserved = reservationList.isReserved(ReservableType.SESSION, entry.id);
                                        const isReservable = availableTokens.isAffordable(
                                            entry.required_token_type,
                                            entry.required_token_amount,
                                            entry.starts_at
                                        );
                                        return (
                                            <ScheduleListItem
                                                key={entry.id}
                                                title={entry.template_name}
                                                reservable={entry}
                                                actionItem={
                                                    <div styleName="actions">
                                                        <Button
                                                            id="session-reserve-button"
                                                            styleName="button"
                                                            type="primary"
                                                            disabled={!isReservable || reserved || isFullSession(entry)}
                                                            size="xs"
                                                            onClick={() =>
                                                                GlobalStatesV3.routing.push(
                                                                    V3Routes.sessionReservation(entry.id)
                                                                )
                                                            }
                                                        >
                                                            {reserved
                                                                ? '予約済'
                                                                : isFullSession(entry)
                                                                    ? '満席'
                                                                    : '選択する'}
                                                        </Button>
                                                        {isReservable && (
                                                            <WaitlistToggleButton
                                                                entry={entry}
                                                                reserved={reserved}
                                                                sessionWaitlistState={this.sessionWaitlistState}
                                                            />
                                                        )}
                                                    </div>
                                                }
                                            />
                                        );
                                    })}
                                </DateGroup>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}
